/*
Copyright 2026 Dexnet / iON.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

/*
 * iON: failures while Element Call starts, which upstream leaves silent.
 *  - audit 4: without config.json the call falls back to defaults and then
 *    fails with MISSING_MATRIX_RTC_TRANSPORT, with nothing in Graylog.
 *  - audit 5: a host that never hands over its client leaves the loading
 *    screen forever.
 *  - audit 12: how long a start takes is not known.
 */

import { useEffect, useState } from "react";

import { type ConfigOptions } from "../config/ConfigOptions";
import { type Graylog } from "../logging/Graylog";

/** How long a widget waits for the host's client before giving up. */
export const HOST_CLIENT_TIMEOUT_MS = 20_000;

/**
 * Used by the embedded package when config.json does not load: the same
 * LiveKit and Graylog the iON configs name (config/config.ion-*.json).
 */
export const EMBEDDED_FALLBACK_CONFIG: ConfigOptions = {
  livekit: { livekit_service_url: "https://7ion.org/livekit/jwt/" },
  graylog: { host: "graylog.dexnet.one", facility: "ion-call", timeout: 1000 },
};

let configMissing: { url: string; status: number } | null = null;
let configState: "loaded" | "fallback" | "defaults" = "loaded";

/**
 * How the config of this page came about: from config.json, from the
 * embedded fallback, or bare defaults (BUGS 154).
 */
export function configLoad(): "loaded" | "fallback" | "defaults" {
  return configState;
}

/**
 * Notes that config.json did not load and gives the config to use instead:
 * the fallback in the embedded package, the defaults elsewhere.
 */
export function configFallback(
  url: string,
  status: number,
  embedded = import.meta.env.VITE_PACKAGE === "embedded",
): ConfigOptions {
  configMissing = { url, status };
  configState = embedded ? "fallback" : "defaults";
  return embedded ? structuredClone(EMBEDDED_FALLBACK_CONFIG) : {};
}

/** Sends a noted config.json failure to Graylog, once. */
export function reportConfigMissing(graylog: Graylog): void {
  if (!configMissing) return;
  graylog.logWarning("config.json not loaded", configMissing);
  configMissing = null;
}

let loadReported = false;

/**
 * Sends to Graylog, once, how long the page took to load until the call
 * screen could start (audit 12, BUGS 125: slow starts in the apps).
 */
export function reportLoaded(
  graylog: Graylog,
  sinceStartMs: number = performance.now(),
): void {
  if (loadReported) return;
  loadReported = true;
  graylog.logInfo("Call loaded", {
    loadSeconds: Math.round(sinceStartMs / 100) / 10,
  });
}

/**
 * Turns waiting for the host's client into an error after
 * {@link HOST_CLIENT_TIMEOUT_MS}; ErrorPage shows it and sends it to Graylog.
 */
export function useHostClientTimeout(waiting: boolean): Error | null {
  const [error, setError] = useState<Error | null>(null);
  useEffect(() => {
    if (!waiting) return;
    const timeout = setTimeout(() => {
      setError(
        new Error(
          `The host did not hand over its client in ${HOST_CLIENT_TIMEOUT_MS} ms`,
        ),
      );
    }, HOST_CLIENT_TIMEOUT_MS);
    return (): void => clearTimeout(timeout);
  }, [waiting]);
  return waiting ? error : null;
}
